import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

export default function BrokerageSection({ onChange }) {
  const [groups, setGroups] = useState(null);
  const [canonInputs, setCanonInputs] = useState({});
  const [busy, setBusy] = useState(null);

  async function load() {
    const { data } = await api.get(`/cleanup/brokerage-groups`);
    setGroups(data.groups || []);
    const inputs = {};
    (data.groups || []).forEach((g) => { inputs[g.canon] = g.suggested; });
    setCanonInputs(inputs);
  }
  useEffect(() => { load(); }, []);

  async function applyGroup(g) {
    const target = (canonInputs[g.canon] || "").trim();
    if (!target) { toast.error("Canonical name is required"); return; }
    setBusy(g.canon);
    try {
      const { data } = await api.post(`/cleanup/brokerage-merge`, {
        canonical_name: target,
        source_names: g.variants.map((v) => v.name),
      });
      toast.success(`Updated ${data.updated_realtors} realtors + ${data.updated_brokerages} brokerages`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  }

  if (groups === null) return <div className="text-sm text-slate-500">Loading…</div>;
  if (groups.length === 0) return <div className="text-sm text-slate-500">No brokerage groups need merging.</div>;
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.canon} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`brokerage-group-${g.canon}`}>
          <div className="text-xs text-slate-500">canonical key: <code>{g.canon}</code> · {g.total_realtors} realtors</div>
          <ul className="mt-2 space-y-1 text-sm">
            {g.variants.map((v) => (
              <li key={v.name} className="flex items-center justify-between">
                <code className="text-slate-700">{v.name}</code>
                <Badge variant="outline">{v.count}</Badge>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <Label className="text-xs">Canonical name</Label>
              <Input
                value={canonInputs[g.canon] || ""}
                onChange={(e) => setCanonInputs({ ...canonInputs, [g.canon]: e.target.value })}
                className="min-h-[44px]"
                data-testid={`brokerage-canonical-input-${g.canon}`}
              />
            </div>
            <Button
              onClick={() => applyGroup(g)}
              disabled={busy === g.canon}
              data-testid={`brokerage-apply-${g.canon}`}
              className="min-h-[44px]"
            >
              <Check className="h-4 w-4 mr-1.5" />
              {busy === g.canon ? "Applying…" : "Apply"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
